/* © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
const isFromApp = (searchStr: string) => {
  const search = new URLSearchParams(searchStr)
  const state = search.get('state')

  if (state) {
    const stateParams = new URLSearchParams(decodeURIComponent(state))
    const from = stateParams.get('from')
    return from === 'app'
  }
  return false
}

const isFromExpired = (searchStr: string) => {
  const search = new URLSearchParams(searchStr)
  const state = search.get('state')

  if (state) {
    const stateParams = new URLSearchParams(decodeURIComponent(state))
    const expired = stateParams.get('expired')
    return expired === 'true'
  }
  return false
}

// https://www.example.com/account?a=b#c=d => #c=d
const getHashFromUrlStr = (url: string) => {
  const hashIndex = url.indexOf('#')
  return hashIndex > -1 ? url.slice(hashIndex) : ''
}
// https://www.example.com/account?a=b#c=d => ?a=b
const getSearchFromUrlStr = (url: string) => {
  const searchIndex = url.indexOf('?')
  const hashIndex = url.indexOf('#')
  return searchIndex > -1 ? url.slice(searchIndex, hashIndex > -1 ? hashIndex : url.length) : ''
}
// https://www.example.com/account?a=b#c=d => https://www.example.com
const getOriginFromUrlStr = (url: string) => {
  const hasOrigin = url.startsWith('http://') || url.startsWith('https://')
  const omittedHeader = url.slice(url.indexOf('//') + 2)
  const originEnd = omittedHeader.indexOf('/')
  return hasOrigin
    ? url.slice(0, url.indexOf('//') + 2) + omittedHeader.slice(0, originEnd > -1 ? originEnd : omittedHeader.length)
    : ''
}
// https://www.example.com/account?a=b#c=d => /account
const getPathnameFromUrlStr = (url: string) => {
  const searchIndex = url.indexOf('?')
  const hashIndex = url.indexOf('#')
  const pathnameStart = getOriginFromUrlStr(url).length
  if (pathnameStart === url.length) return '/'
  return url.slice(
    pathnameStart,
    searchIndex === -1 && hashIndex === -1 ? url.length : searchIndex > -1 ? searchIndex : hashIndex,
  )
}

const urlToObject = (url: string) => {
  return {
    origin: getOriginFromUrlStr(url),
    pathname: getPathnameFromUrlStr(url),
    search: getSearchFromUrlStr(url),
    hash: getHashFromUrlStr(url),
  }
}

const parsedUrl = (url?: string) => {
  const { origin, pathname, search, hash } = url ? urlToObject(url) : window.location
  const searchObj = new URLSearchParams(search)

  return {
    origin,
    pathname,
    search,
    hash,
    getSearchParam: (key: string) => searchObj.get(key),
    omitSearchParams: (keys: string[]) => {
      for (const k of searchObj) {
        if (keys.includes(k[0])) {
          searchObj.delete(k[0])
        }
      }
      return Array.from(searchObj).length ? `?${searchObj.toString()}` : ''
    },
    getHrefWithOmitSearchParams: (keys: string[]) => {
      return origin + pathname + parsedUrl(url).omitSearchParams(keys) + hash
    },
    getPathnameTailWithOmitSearchParams: (keys: string[]) => {
      return pathname + parsedUrl(url).omitSearchParams(keys) + hash
    },
  }
}

export {
  parsedUrl,
  getHashFromUrlStr,
  getSearchFromUrlStr,
  getOriginFromUrlStr,
  getPathnameFromUrlStr,
  urlToObject,
  isFromExpired,
  isFromApp,
}
